import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Grid, Header, Popup } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

import BBuildingContactPersonListContainer from './BBuildingContactPersonListContainer';
import CPHazardsContainer from '../ChemicalProduct/ChemicalProductDetails/GeneralInformation/CPHazardsContainer';
import BRisksContainer from './BRisksContainer';
import ReadOnlyMapContainer from './ReadOnlyMapContainer';
import BImportantInformationContainer from './BImportantInformationContainer';
import { compose } from 'redux';
import connect from 'react-redux/es/connect/connect';
import { reportsActions } from '../../behavior/reports';
import { GET_BUILDINGS_REPORT_PATH } from '../../behavior/reports/types';
import { useTitleDispatch } from '../TitleContextProvider';
import { CurrentPageQR } from 'react-lib/dist/components-es';
import BInventoryRecordsContactPersonListContainer from './BInventoryRecordsContactPersonListContainer';
import BBuildingDetailsContainer from './BBuildingDetailsContainer';
import { LOGO_IMAGE_PATH } from '../../constants';
import i18n from 'i18next';
import { gridTemplatesOptions } from '../../objects/StickerGridTemplate';
import DownloadStickersModal from '../common/DownloadStickersModal';
import { identityFunction } from '../../utils';
import ButtonWrapper from '../common/ButtonWrapper';
import { locationsActions } from '../../behavior/locations';

const BuildingSummaryForm = ({
  getBuildingReport,
  formikProps,
  detailContainerProps,
  t,
  isReportLoading,
}) => {
  const props = { ...formikProps, ...detailContainerProps };
  const { values } = formikProps;
  const { buildingNum } = detailContainerProps;
  const pageTitle = t('building.summary.page.building.summary.container.title', {
    locationInfo: values.location.locationInfoWithAcronym,
  });
  const dispatch = useTitleDispatch();
  const [mapImage, setMapImage] = useState(null);

  useEffect(() => {
    dispatch({
      pageTitle,
    });
  }, [i18n.language]);

  const downloadQRStickersButton = useMemo(
    () => setOpenModal =>
      (
        <ButtonWrapper
          label={t('download.stickers.button.label')}
          icon="qrcode"
          type="button"
          className="mini"
          onClick={() => setOpenModal(true)}
        />
      ),
    []
  );

  const selectedItemsSupplier = useMemo(
    () => () => [values?.location].map(building => building.id),
    [values?.location]
  );

  const itemsLabelsSupplier = useMemo(
    () => () =>
      Object.assign(
        {},
        ...[values?.location].map(building => ({
          [building.id]: `BUILDING: ${building?.locationInfoWithAcronym}`,
        }))
      ),
    [values?.location]
  );

  const urlTemplateMapper = useMemo(() => ignored => `${window.location}`, [window.location]);

  const getButtons = () => {
    let buttons = [];
    buttons.push(
      <Popup
        key="export.pdf.button.tooltip"
        trigger={
          <Button
            type="button"
            icon="file pdf"
            key="export.pdf.button"
            color="grey"
            className="mini"
            onClick={() => getBuildingReport([{ mapImage, buildingNum }])}
            loading={isReportLoading}
            disabled={!mapImage && !!window.CERNMap}
          />
        }
        on={['hover']}
        size="mini"
        content={t('details.page.export.pdf.button.tooltip')}
        position="bottom left"
      />
    );

    buttons.push(
      <DownloadStickersModal
        key="export.qr.stickers.button"
        downloadStickersTriggerComponent={downloadQRStickersButton}
        createPDFActionMapper={locationsActions.createBuildingsQRStickersPdf}
        selectedItemsSupplier={selectedItemsSupplier}
        itemsLabelsSupplier={itemsLabelsSupplier}
        itemsLabelResultMapper={identityFunction}
        urlTemplateMapper={urlTemplateMapper}
        gridTemplateCreatorOptions={gridTemplatesOptions}
        downloadFileName={`building-${buildingNum}-qr-sticker.pdf`.replace(/\s+/g, '')}
      />
    );

    return buttons;
  };

  return (
    <Form className="buildingSummaryForm smallScreenNoMargin">
      <Grid unstackable="true">
        <Grid.Column
          className="detailsGridCol buildingSummaryTitle"
          mobile={12}
          computer={8}
          tablet={12}
        >
          <Header as="h2">{pageTitle}</Header>
          <BBuildingDetailsContainer {...props} />
          <div className="buildingSummaryRisksButtons">
            <b>{<BRisksContainer {...props} />}</b>
            {getButtons()}
          </div>
        </Grid.Column>
        <Grid.Column
          className="detailsGridCol buildingSummaryQrcode"
          mobile={4}
          computer={8}
          tablet={4}
        >
          <CurrentPageQR
            titleProp={pageTitle}
            qrSize={96}
            showTitleInput={false}
            showTitle={false}
            float="right"
            logoImagePath={LOGO_IMAGE_PATH}
          />
        </Grid.Column>
      </Grid>
      <Grid stackable columns={2} className="buildingSummaryDetailsGrid smallScreenReducePadding">
        <Grid.Column className="detailsGridCol">
          <Grid className="">
            <Grid.Row columns={2}>
              <Grid.Column width={16}>
                <CPHazardsContainer
                  {...props}
                  hazards={values.hazards || []}
                  arrayName="hazards"
                  disabled={true}
                  showAllHazards={false}
                />
              </Grid.Column>
              <Grid.Column
                computer={8}
                mobile={16}
                only="computer mobile"
                className="detailsGridCol buildingContactPersonList"
              >
                <BBuildingContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
              <Grid.Column
                computer={8}
                mobile={16}
                only="computer mobile"
                className="detailsGridCol inventoryRecordsContactPersonList"
              >
                <BInventoryRecordsContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
              <Grid.Column width={16}>
                <BImportantInformationContainer {...props} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>

        <Grid.Column className="detailsGridCol">
          <Grid className="">
            <Grid.Row>
              <Grid.Column width={16}>
                <ReadOnlyMapContainer location={values.location} setMapImage={setMapImage} />
              </Grid.Column>
              <Grid.Column
                width={16}
                only="tablet"
                className="detailsGridCol buildingContactPersonList"
              >
                <BBuildingContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
              <Grid.Column
                width={16}
                only="tablet"
                className="detailsGridCol inventoryRecordsContactPersonList"
              >
                <BInventoryRecordsContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid>
    </Form>
  );
};

const mapStateToProps = state => ({
  isReportLoading: state.reports.isReportLoading,
});

const mapDispatchToProps = dispatch => ({
  getBuildingReport: locReportReqDtoList =>
    dispatch(reportsActions.getReport(GET_BUILDINGS_REPORT_PATH, null, locReportReqDtoList)),
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(BuildingSummaryForm);
